import React from "react";
import { cn } from "../cn";

export type PageHeaderProps = {
  title: React.ReactNode;
  /** Short supporting sentence. Optional — most pages do not need one. */
  description?: React.ReactNode;
  /** An overline rendered above the title, e.g. the section the page sits in. */
  eyebrow?: React.ReactNode;
  /** Primary and secondary actions for the page, right-aligned on desktop. */
  actions?: React.ReactNode;
  className?: string;
};

/**
 * The single `h1` of a page, its supporting copy and its actions.
 *
 * Having one component own this is what keeps heading structure correct
 * (WCAG 1.3.1): pages were previously free to open with an `h3`, or with two
 * competing `h1`s once a hero and a section title were both present.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  eyebrow,
  actions,
  className,
}) => (
  <header
    className={cn(
      "flex flex-col gap-4 md:flex-row md:items-end md:justify-between",
      className
    )}
  >
    <div className="min-w-0 space-y-2">
      {eyebrow && (
        <span className="block text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
          {eyebrow}
        </span>
      )}
      <h1 className="text-2xl font-semibold tracking-tight text-content-primary sm:text-3xl">
        {title}
      </h1>
      {description && (
        <p className="max-w-2xl text-sm leading-relaxed text-content-secondary">
          {description}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex shrink-0 flex-wrap items-center gap-3">{actions}</div>
    )}
  </header>
);

export default PageHeader;
